import React, { useState } from 'react';
import { Calendar, ChevronDown } from 'lucide-react';

export type DateRange = {
  startDate: Date | null;
  endDate: Date | null;
  period: 'all' | '7days' | '30days' | 'custom';
};

interface DateFilterProps {
  onChange: (dateRange: DateRange) => void;
  initialValue?: DateRange;
}

export function DateFilter({ onChange, initialValue }: DateFilterProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [dateRange, setDateRange] = useState<DateRange>(
    initialValue || {
      startDate: null,
      endDate: null,
      period: 'all'
    }
  );
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');

  const handlePeriodChange = (period: DateRange['period']) => {
    let newDateRange: DateRange;
    const now = new Date();
    
    switch (period) {
      case '7days':
        const sevenDaysAgo = new Date();
        sevenDaysAgo.setDate(now.getDate() - 7);
        newDateRange = {
          startDate: sevenDaysAgo,
          endDate: now,
          period
        };
        break;
      
      case '30days':
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(now.getDate() - 30);
        newDateRange = {
          startDate: thirtyDaysAgo,
          endDate: now,
          period
        };
        break;
      
      case 'all':
        newDateRange = {
          startDate: null,
          endDate: null,
          period
        };
        break;
      
      case 'custom':
        newDateRange = {
          ...dateRange,
          period
        };
        break;
      
      default:
        return;
    }
    
    setDateRange(newDateRange);
    if (period !== 'custom') {
      onChange(newDateRange);
      setIsOpen(false);
    }
  };

  const handleCustomDateApply = () => {
    if (customStart && customEnd) {
      const newDateRange: DateRange = {
        startDate: new Date(customStart),
        endDate: new Date(customEnd),
        period: 'custom'
      };
      setDateRange(newDateRange);
      onChange(newDateRange);
      setIsOpen(false);
    }
  };

  const formatDateRange = () => {
    switch (dateRange.period) {
      case 'all':
        return 'All Time';
      case '7days':
        return 'Last 7 Days';
      case '30days':
        return 'Last 30 Days';
      case 'custom':
        if (dateRange.startDate && dateRange.endDate) {
          return `${dateRange.startDate.toLocaleDateString()} - ${dateRange.endDate.toLocaleDateString()}`;
        }
        return 'Custom Range';
      default:
        return 'Select Period';
    }
  };

  // Calculate max date (today) for date inputs
  const today = new Date().toISOString().split('T')[0];
  const thirtyDaysAgo = new Date();
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
  const minDate = thirtyDaysAgo.toISOString().split('T')[0];

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
      >
        <Calendar className="h-4 w-4 mr-2 text-gray-400" />
        <span>{formatDateRange()}</span>
        <ChevronDown className="ml-2 h-4 w-4 text-gray-400" />
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
          <div className="p-4">
            <div className="space-y-3">
              <label className="flex items-center space-x-3 cursor-pointer">
                <input
                  type="radio"
                  name="period"
                  checked={dateRange.period === 'all'}
                  onChange={() => handlePeriodChange('all')}
                  className="text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm font-medium text-gray-900">All Time</span>
              </label>
              
              <label className="flex items-center space-x-3 cursor-pointer">
                <input
                  type="radio"
                  name="period"
                  checked={dateRange.period === '7days'}
                  onChange={() => handlePeriodChange('7days')}
                  className="text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm font-medium text-gray-900">Last 7 Days</span>
              </label>
              
              <label className="flex items-center space-x-3 cursor-pointer">
                <input
                  type="radio"
                  name="period"
                  checked={dateRange.period === '30days'}
                  onChange={() => handlePeriodChange('30days')}
                  className="text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm font-medium text-gray-900">Last 30 Days</span>
              </label>
              
              <label className="flex items-center space-x-3 cursor-pointer">
                <input
                  type="radio"
                  name="period"
                  checked={dateRange.period === 'custom'}
                  onChange={() => handlePeriodChange('custom')}
                  className="text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm font-medium text-gray-900">Custom Range</span>
              </label>

              {dateRange.period === 'custom' && (
                <div className="mt-4 space-y-3 border-t pt-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Start Date
                    </label>
                    <input
                      type="date"
                      value={customStart}
                      onChange={(e) => setCustomStart(e.target.value)}
                      min={minDate}
                      max={today}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      End Date
                    </label>
                    <input
                      type="date"
                      value={customEnd}
                      onChange={(e) => setCustomEnd(e.target.value)}
                      min={customStart || minDate}
                      max={today}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  
                  <button
                    onClick={handleCustomDateApply}
                    disabled={!customStart || !customEnd}
                    className="w-full px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Apply
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}